import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import {
  Plus,
  Minus,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  TrendingUp,
  Target,
  Star,
  MessageCircle,
  Tv,
  PaintBucket,
  Volume2,
  Palette,
  Type,
  Image,
} from "lucide-react";
import OriginalResponsesDisplay from "./OriginalResponsesDisplay";
import ScaleBar from "./ScaleBar";

const CharacteristicCard = ({
  title,
  value,
  index,
  isExpanded,
  onToggle,
  palette,
  questionTitles = {},
}) => {
  // Support value as object with value, leftLabel, rightLabel (from transformResponseForBrandProfile)
  let actualValue = value;
  let customLeftLabel = undefined;
  let customRightLabel = undefined;
  if (typeof value === "object" && value !== null && "value" in value) {
    actualValue = value.value;
    customLeftLabel = value.leftLabel;
    customRightLabel = value.rightLabel;
  }

  // Different background colors for visual variety
  const bgGradients = [
    "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.03) 100%)",
    "linear-gradient(135deg, rgba(var(--primary-color-rgb), 0.12) 0%, rgba(var(--primary-color-rgb), 0.06) 100%)",
    "linear-gradient(135deg, rgba(var(--secondary-color-rgb), 0.12) 0%, rgba(var(--secondary-color-rgb), 0.06) 100%)",
    "linear-gradient(135deg, rgba(var(--accent-color-rgb), 0.12) 0%, rgba(var(--accent-color-rgb), 0.06) 100%)",
  ];

  const gradient = bgGradients[index % bgGradients.length];
  const delayAnimation = index * 0.1;

  // Detect if value might be a sliding scale number (1-5, 1-10, or any N)
  const isScaleValue = useMemo(() => {
    if (
      typeof actualValue === "number" &&
      actualValue >= 1 &&
      actualValue <= 10
    ) {
      return true;
    }
    if (typeof actualValue === "string") {
      const num = parseFloat(actualValue);
      if (!isNaN(num) && num >= 1 && num <= 10) {
        return true;
      }
    }
    return false;
  }, [actualValue]);

  // Dynamically determine the scale max (5, 10, or other)
  const getScaleMax = () => {
    if (typeof actualValue === "number") {
      if (actualValue <= 5) return 5;
      if (actualValue <= 10) return 10;
    }
    if (typeof actualValue === "string") {
      const num = parseFloat(actualValue);
      if (!isNaN(num)) {
        if (num <= 5) return 5;
        if (num <= 10) return 10;
      }
    }
    return 5; // fallback
  };
  const scaleMax = getScaleMax();

  // Default scale labels, but prefer custom if present
  const getScaleLabels = (characteristic) => {
    if (customLeftLabel || customRightLabel) {
      return {
        leftLabel: customLeftLabel || "",
        rightLabel: customRightLabel || "",
      };
    }
    const lowerChar = characteristic.toLowerCase();
    if (lowerChar.includes("formal") || lowerChar.includes("casual")) {
      return { leftLabel: "Casual", rightLabel: "Formal" };
    }
    if (lowerChar.includes("playful") || lowerChar.includes("serious")) {
      return { leftLabel: "Serious", rightLabel: "Playful" };
    }
    if (lowerChar.includes("modern") || lowerChar.includes("traditional")) {
      return { leftLabel: "Traditional", rightLabel: "Modern" };
    }
    if (lowerChar.includes("loud") || lowerChar.includes("quiet")) {
      return { leftLabel: "Quiet", rightLabel: "Loud" };
    }
    if (lowerChar.includes("bright") || lowerChar.includes("dark")) {
      return { leftLabel: "Dark", rightLabel: "Bright" };
    }
    return {
      leftLabel: `Low ${title}`,
      rightLabel: `High ${title}`,
    };
  };

  const scaleLabels = useMemo(
    () => getScaleLabels(title),
    [title, customLeftLabel, customRightLabel]
  );

  // Format the text for better readability
  const formatText = (text) => {
    if (!text) return ["No information available"];
    if (typeof text !== "string") {
      if (typeof text === "object") {
        try {
          return [JSON.stringify(text, null, 2)];
        } catch (e) {
          return ["Complex object"];
        }
      }
      return [String(text)];
    }
    if (text.length < 200) return [text];
    const sentences = text.split(/(?<=[.!?])\s+/);
    const paragraphs = [];
    let currentParagraph = "";
    sentences.forEach((sentence) => {
      if (currentParagraph.length + sentence.length > 150) {
        paragraphs.push(currentParagraph);
        currentParagraph = sentence;
      } else {
        currentParagraph += (currentParagraph ? " " : "") + sentence;
      }
    });
    if (currentParagraph) {
      paragraphs.push(currentParagraph);
    }
    return paragraphs;
  };

  // Display placeholder text if value is empty or undefined
  // Ensure displayValue is always a string to avoid React rendering errors
  const displayValue = (() => {
    if (actualValue === undefined || actualValue === null) {
      return "No information available";
    }
    if (typeof actualValue === "object") {
      // If it's still an object, extract value property or stringify
      if (typeof actualValue.value !== "undefined") {
        return String(actualValue.value);
      }
      if (actualValue.description) {
        return actualValue.description;
      }
      try {
        return JSON.stringify(actualValue);
      } catch (e) {
        return "Complex object";
      }
    }
    return String(actualValue);
  })();
  const paragraphs = formatText(displayValue);

  // Helper function to determine significance class for scale values
  const getSignificanceClass = (val) => {
    if (val >= 4) return "text-green-400 font-bold";
    if (val <= 2) return "text-blue-400 font-bold";
    return "";
  };

  // Get numeric value for scale if applicable
  const numericValue = isScaleValue
    ? typeof actualValue === "number"
      ? actualValue
      : parseFloat(actualValue)
    : Math.ceil(scaleMax / 2); // Default to middle value

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: delayAnimation }}
      onClick={onToggle}
      className={`rounded-xl overflow-hidden backdrop-blur-sm border border-white/10 cursor-pointer transition-all duration-300 ${
        isExpanded ? "col-span-full" : ""
      }`}
      style={{
        background: gradient,
      }}
      whileHover={{
        scale: isExpanded ? 1 : 1.02,
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
      }}
    >
      <motion.div
        className="p-6"
        initial={false}
        animate={{
          padding: isExpanded ? "1.5rem" : "1.5rem",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <motion.div
              className="w-10 h-10 rounded-full flex items-center justify-center mr-3 text-white"
              style={{ backgroundColor: "var(--primary-color)" }}
              whileHover={{ scale: 1.1 }}
            >
              <TrendingUp size={20} />
            </motion.div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.3 }}
            className="text-white/60"
          >
            <ChevronRight size={20} />
          </motion.div>
        </div>

        <AnimatePresence>
          {!isExpanded && (
            <motion.div
              className="text-white/70 text-sm"
              exit={{ opacity: 0, height: 0 }}
            >
              {isScaleValue ? (
                <div>
                  <ScaleBar
                    value={numericValue}
                    min={1}
                    max={scaleMax}
                    leftLabel={scaleLabels.leftLabel}
                    rightLabel={scaleLabels.rightLabel}
                    palette={palette}
                    label={title}
                    customTitle={questionTitles[title]}
                  />
                  {actualValue && typeof actualValue === "object" && actualValue.notes && actualValue.notesVisible !== false && (
                    <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
                      <p className="text-white/70 text-sm whitespace-pre-wrap">{actualValue.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="line-clamp-3">{displayValue}</div>
              )}
            </motion.div>
          )}

          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 space-y-4"
            >
              {isScaleValue ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <ScaleBar
                    value={numericValue}
                    min={1}
                    max={scaleMax}
                    leftLabel={scaleLabels.leftLabel}
                    rightLabel={scaleLabels.rightLabel}
                    palette={palette}
                    label={title}
                    customTitle={questionTitles[title]}
                  />
                  {actualValue && typeof actualValue === "object" && actualValue.notes && actualValue.notesVisible !== false && (
                    <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
                      <p className="text-white/70 text-sm whitespace-pre-wrap">{actualValue.notes}</p>
                    </div>
                  )}
                </motion.div>
              ) : (
                <div className="space-y-4">
                  {paragraphs.map((paragraph, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="text-white/80 leading-relaxed"
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

const DynamicBrandCharacteristicsSection = ({
  characteristics,
  scrollProgress,
  palette,
  profileId,
  backgroundImage,
  sectionIndex = 0,
  questionTitles = {},
  responseData,
  questionnaireData,
}) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Enhanced animation controls
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.7, 0.9, 0.7]);

  // Determine if this section should have the background on the left or right
  const isLeftAligned = sectionIndex % 2 === 0;

  // Cards per slide for mobile view
  const CARDS_PER_SLIDE = 2;

  // Set up CSS Variables for colors
  useEffect(() => {
    if (palette) {
      document.documentElement.style.setProperty(
        "--primary-color",
        palette.primary
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        palette.secondary
      );
      document.documentElement.style.setProperty(
        "--accent-color",
        palette.accent
      );

      // Helper function to convert hex to RGB for gradients
      const hexToRgb = (hex) => {
        // Remove # if present
        hex = hex.replace("#", "");

        // Convert 3-digit hex to 6-digits
        if (hex.length === 3) {
          hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }

        // Extract r, g, b values
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `${r}, ${g}, ${b}`;
      };

      // Set RGB values for CSS variables
      try {
        document.documentElement.style.setProperty(
          "--primary-color-rgb",
          hexToRgb(palette.primary)
        );
        document.documentElement.style.setProperty(
          "--secondary-color-rgb",
          hexToRgb(palette.secondary)
        );
        document.documentElement.style.setProperty(
          "--accent-color-rgb",
          hexToRgb(palette.accent)
        );

        // Add color variants
        document.documentElement.style.setProperty(
          "--primary-color-20",
          `rgba(${hexToRgb(palette.primary)}, 0.2)`
        );
        document.documentElement.style.setProperty(
          "--primary-color-30",
          `rgba(${hexToRgb(palette.primary)}, 0.3)`
        );
      } catch (e) {
        console.error("Error setting RGB color values:", e);
      }
    }
  }, [palette]);

  // Group characteristics into categories if they exist in the data
  const characteristicEntries = Object.entries(characteristics);

  // Map category names to icons
  const categoryIcons = {
    "Brand Personality": TrendingUp,
    "Visual Elements": Palette,
    "Voice & Tone": MessageCircle,
    "General Characteristics": Target,
    Typography: Type,
    Color: PaintBucket,
    Imagery: Image,
    Sound: Volume2,
    Media: Tv,
  };

  // Create categories based on question patterns or explicit categories
  const categorizeCharacteristics = () => {
    const categories = {
      "Brand Personality": {},
      "Visual Elements": {},
      "Voice & Tone": {},
      "General Characteristics": {},
    };

    characteristicEntries.forEach(([key, value]) => {
      const lowerKey = key.toLowerCase();

      if (
        lowerKey.includes("personality") ||
        lowerKey.includes("trait") ||
        lowerKey.includes("character") ||
        lowerKey.includes("feel") ||
        lowerKey.includes("emotion")
      ) {
        categories["Brand Personality"][key] = value;
      } else if (
        lowerKey.includes("color") ||
        lowerKey.includes("visual") ||
        lowerKey.includes("design") ||
        lowerKey.includes("logo") ||
        lowerKey.includes("image") ||
        lowerKey.includes("photo")
      ) {
        categories["Visual Elements"][key] = value;
      } else if (
        lowerKey.includes("voice") ||
        lowerKey.includes("tone") ||
        lowerKey.includes("speak") ||
        lowerKey.includes("language") ||
        lowerKey.includes("word") ||
        lowerKey.includes("communication")
      ) {
        categories["Voice & Tone"][key] = value;
      } else {
        categories["General Characteristics"][key] = value;
      }
    });

    // Filter out empty categories
    return Object.fromEntries(
      Object.entries(categories).filter(
        ([_, values]) => Object.keys(values).length > 0
      )
    );
  };

  const categorizedCharacteristics = categorizeCharacteristics();

  // Prepare mobile slides
  const prepareMobileSlides = () => {
    const allCards = [];

    // Flatten all categories into single array of cards
    Object.entries(categorizedCharacteristics).forEach(
      ([category, items], catIndex) => {
        const cards = Object.entries(items).map(([key, value], index) => {
          // Use displayTitle if available (for duplicate titles with question IDs)
          const displayKey = (typeof value === "object" && value !== null && value.displayTitle) 
            ? value.displayTitle 
            : key;
          return {
            title: displayKey, // Use display title for duplicate titles
            value: value,
            category: category,
            catIndex: catIndex,
            index: index,
          };
        });

        allCards.push(...cards);
      }
    );

    // Group into slides
    const slides = [];
    for (let i = 0; i < allCards.length; i += CARDS_PER_SLIDE) {
      slides.push(allCards.slice(i, i + CARDS_PER_SLIDE));
    }

    return slides;
  };

  const mobileSlides = useMemo(
    () => prepareMobileSlides(),
    [categorizedCharacteristics]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCategory = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
    setExpandedCardIndex(null); // Reset expanded card when toggling category
  };

  const toggleCard = (categoryKey, index) => {
    setExpandedCardIndex(
      expandedCardIndex === `${categoryKey}-${index}`
        ? null
        : `${categoryKey}-${index}`
    );
  };

  const nextSlide = () => {
    setActiveSlide((prev) => (prev < mobileSlides.length - 1 ? prev + 1 : 0));
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev > 0 ? prev - 1 : mobileSlides.length - 1));
  };

  return (
    <motion.section
      ref={sectionRef}
      className="min-h-screen relative flex items-center justify-center py-24 overflow-hidden"
      style={{
        opacity,
        backgroundColor: "rgba(5, 8, 15, 0.95)", // Much darker background
      }}
    >
      {/* Background image with transparency mask blending */}
      {backgroundImage && (
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <motion.div
            className={`absolute inset-0 h-full bg-center bg-cover bg-no-repeat`}
            style={{
              backgroundImage: `url(${backgroundImage})`,
              opacity: bgOpacity,
              filter: "contrast(1.2) brightness(1.15)",
              mixBlendMode: "soft-light",
              width: "75%",
              left: isLeftAligned ? "0" : "auto",
              right: isLeftAligned ? "auto" : "0",
              maskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
              WebkitMaskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
            }}
          />
        </div>
      )}

      {/* Content container with proper positioning */}
      <div
        className={`relative z-10 w-full max-w-7xl mx-auto px-4 ${
          isLeftAligned ? "ml-auto" : "mr-auto"
        }`}
      >
        <motion.h2
          className="text-5xl md:text-7xl mb-16 font-bold text-white"
          style={{ textShadow: "0 2px 6px rgba(0, 0, 0, 0.5)" }}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Brand Characteristics
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-white/60 max-w-3xl mb-12 text-xl"
        >
          These are the key attributes and characteristics that define the brand
          identity and personality.
        </motion.p>

        {/* AI-Generated Narrative Section */}
        {profileId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center mb-6">
              <motion.div
                className="w-12 h-12 rounded-full flex items-center justify-center mr-4 text-white"
                style={{ backgroundColor: "var(--primary-color)" }}
                whileHover={{ scale: 1.1 }}
              >
                <MessageCircle size={24} />
              </motion.div>
              <h3 className="text-3xl font-bold text-white">
                Characteristic Analysis
              </h3>
            </div>

            <OriginalResponsesDisplay
              sectionType="brandCharacteristics"
              responseData={responseData}
              questionnaireData={questionnaireData}
              textColor="rgba(255, 255, 255, 0.9)"
              accentColor="var(--primary-color)"
            />
          </motion.div>
        )}

        {/* Desktop View: Categories */}
        <div className="hidden md:block space-y-16">
          {Object.entries(categorizedCharacteristics).map(
            ([categoryKey, categoryValues], catIndex) => (
              <div key={categoryKey} className="mb-16">
                {/* Category header */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + catIndex * 0.1 }}
                  viewport={{ once: true }}
                  onClick={() => toggleCategory(categoryKey)}
                  className="flex items-center justify-between mb-8 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className="flex items-center">
                    <motion.div
                      className="w-12 h-12 rounded-full flex items-center justify-center mr-4 text-white"
                      style={{ backgroundColor: "var(--primary-color)" }}
                    >
                      {categoryIcons[categoryKey] ? (
                        React.createElement(categoryIcons[categoryKey], {
                          size: 24,
                        })
                      ) : (
                        <Target size={24} />
                      )}
                    </motion.div>
                    <h3 className="text-3xl font-bold text-white">
                      {categoryKey}
                    </h3>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    {expandedCategory === categoryKey ? (
                      <Minus size={20} className="text-white" />
                    ) : (
                      <Plus size={20} className="text-white" />
                    )}
                  </div>
                </motion.div>

                {/* Category content */}
                <AnimatePresence>
                  {(expandedCategory === categoryKey ||
                    expandedCategory === null) && (
                    <motion.div
                      initial={
                        expandedCategory !== null
                          ? { opacity: 0, height: 0 }
                          : { opacity: 1 }
                      }
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4 }}
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                    >
                      {Object.entries(categoryValues).map(
                        ([key, value], index) => {
                          // Use displayTitle if available (for duplicate titles with question IDs)
                          const displayKey = (typeof value === "object" && value !== null && value.displayTitle) 
                            ? value.displayTitle 
                            : key;
                          return (
                            <CharacteristicCard
                              key={key}
                              title={displayKey}
                              value={value}
                              index={index}
                              isExpanded={
                                expandedCardIndex === `${categoryKey}-${index}`
                              }
                              onToggle={() => toggleCard(categoryKey, index)}
                              palette={palette}
                              questionTitles={questionTitles}
                            />
                          );
                        }
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          )}
        </div>

        {/* Mobile View: Cards Carousel */}
        <div className="md:hidden space-y-8">
          <div className="flex justify-between items-center mb-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={prevSlide}
              className="bg-white/10 rounded-full p-2"
              disabled={mobileSlides.length <= 1}
            >
              <ChevronLeft
                className={`${
                  mobileSlides.length <= 1 ? "text-white/30" : "text-white"
                }`}
                size={20}
              />
            </motion.button>

            <div className="text-white/60 text-sm">
              {mobileSlides.length > 0
                ? `${activeSlide + 1} / ${mobileSlides.length}`
                : "0 / 0"}
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={nextSlide}
              className="bg-white/10 rounded-full p-2"
              disabled={mobileSlides.length <= 1}
            >
              <ChevronRight
                className={`${
                  mobileSlides.length <= 1 ? "text-white/30" : "text-white"
                }`}
                size={20}
              />
            </motion.button>
          </div>

          <AnimatePresence mode="wait">
            {mobileSlides.length > 0 ? (
              <motion.div
                key={activeSlide}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.3 }}
                className="space-y-4"
              >
                {mobileSlides[activeSlide].map((card, index) => (
                  <CharacteristicCard
                    key={`${card.category}-${card.index}-mobile`}
                    title={card.title}
                    value={card.value}
                    index={index}
                    isExpanded={
                      expandedCardIndex === `mobile-${activeSlide}-${index}`
                    }
                    onToggle={() =>
                      setExpandedCardIndex(
                        expandedCardIndex === `mobile-${activeSlide}-${index}`
                          ? null
                          : `mobile-${activeSlide}-${index}`
                      )
                    }
                    palette={palette}
                    questionTitles={questionTitles}
                  />
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-8 text-center bg-white/5 rounded-lg border border-white/10"
              >
                <p className="text-white/70">
                  No characteristics are available to display.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pagination dots - only show if there are multiple slides */}
          {mobileSlides.length > 1 && (
            <div className="flex justify-center mt-6 space-x-2">
              {mobileSlides.map((_, index) => (
                <motion.button
                  key={index}
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor:
                      activeSlide === index
                        ? "var(--primary-color)"
                        : "rgba(255, 255, 255, 0.3)",
                  }}
                  onClick={() => setActiveSlide(index)}
                  whileHover={{ scale: 1.3 }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
};

export default DynamicBrandCharacteristicsSection;
